import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import AppNav from "@/components/nav/AppNav";
import ReelComposer from "@/components/reels/ReelComposer";
import ShareSheet from "@/components/reels/ShareSheet";
import { Heart, MessageCircle, Send, Plus, Trash2, VolumeX, Volume2, Eye, Lock, Globe, Loader2, ChevronDown } from "lucide-react";

// Mirror of the filters defined in ReelComposer. Keeps this file
// self-contained (no cross-import cycle) — the filter key set is tiny + stable.
const FILTER_CSS = {
  none:   "none",
  gray:   "grayscale(1)",
  sepia:  "sepia(0.8) saturate(1.1)",
  bright: "brightness(1.2) contrast(1.05) saturate(1.15)",
  cool:   "contrast(1.05) hue-rotate(-15deg) saturate(1.1)",
  warm:   "contrast(1.05) hue-rotate(15deg) saturate(1.15) brightness(1.05)",
};
const cssFor = (key) => FILTER_CSS[key] || "none";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const absUrl = (u) => (!u ? "" : (u.startsWith("http") ? u : `${BACKEND}${u}`));

function humanAgo(iso) {
  try {
    const d = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (d < 60) return "az önce";
    if (d < 3600) return `${Math.floor(d / 60)} dk`;
    if (d < 86400) return `${Math.floor(d / 3600)} sa`;
    return `${Math.floor(d / 86400)} gün`;
  } catch { return ""; }
}

function ReelCard({ reel, active, near, onLike, onOpenMessage, onOpenShare, onDelete, onView, muted, onToggleMute }) {
  const videoRef = useRef(null);
  const viewedRef = useRef(false);
  const [mediaError, setMediaError] = useState(false);
  const [loading, setLoading] = useState(reel.media_type === "video");
  const [retryToken, setRetryToken] = useState(0); // bump to force <video> remount

  // Only mount video src when this card is active OR immediately adjacent
  // (near=true from the parent). This avoids preloading N videos at once,
  // which was making the feed lag and sometimes never load.
  const shouldLoadMedia = active || near;

  useEffect(() => {
    if (!active) {
      try { videoRef.current?.pause(); } catch {}
      return;
    }
    // Record view once per session per reel
    if (!viewedRef.current) {
      viewedRef.current = true;
      onView?.(reel.id);
    }
    // Play video when active
    if (reel.media_type === "video" && videoRef.current) {
      const el = videoRef.current;
      el.muted = muted;
      el.play().catch(() => {});
    }
  }, [active, reel.id, reel.media_type, muted, onView]);

  return (
    <div
      className="snap-start relative w-full h-full flex items-center justify-center bg-[#0A0A0A]"
      data-testid={`reel-${reel.id}`}
    >
      {/* Media background */}
      {reel.media_type === "video" ? (
        shouldLoadMedia ? (
          <>
            {reel.thumbnail_url && (
              <img
                src={absUrl(reel.thumbnail_url)}
                alt=""
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${loading ? "opacity-100" : "opacity-0"}`}
                aria-hidden
              />
            )}
            <video
              ref={videoRef}
              key={`${reel.id}-${retryToken}`}
              src={absUrl(reel.media_url)}
              poster={reel.thumbnail_url ? absUrl(reel.thumbnail_url) : undefined}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: cssFor(reel.filter) }}
              loop
              muted={muted}
              playsInline
              autoPlay={active}
              preload={active ? "auto" : "metadata"}
              onClick={onToggleMute}
              onCanPlay={() => { setLoading(false); setMediaError(false); }}
              onLoadedData={() => { setLoading(false); setMediaError(false); }}
              onError={() => { setMediaError(true); setLoading(false); }}
            />
          </>
        ) : reel.thumbnail_url ? (
          <img src={absUrl(reel.thumbnail_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" aria-hidden />
        ) : (
          <div className="absolute inset-0 bg-[#111]" />
        )
      ) : (
        <img
          src={absUrl(reel.media_url)}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          onError={() => setMediaError(true)}
        />
      )}

      {/* Loading spinner while active video buffers */}
      {reel.media_type === "video" && active && loading && !mediaError && (
        <div className="absolute inset-0 flex items-center justify-center z-[5]" data-testid={`reel-loading-${reel.id}`}>
          <div className="w-12 h-12 rounded-full border-4 border-white/80 border-t-transparent animate-spin" />
        </div>
      )}

      {/* Media error fallback with retry + open-in-new-tab */}
      {mediaError && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#1a1a1a] z-[5] p-6" data-testid={`reel-error-${reel.id}`}>
          <div className="text-center max-w-xs">
            <div className="text-4xl mb-2">📵</div>
            <div className="font-black text-sm">Medya yüklenemedi</div>
            <div className="text-xs text-white/60 mt-1 mb-3">
              Video tarayıcın tarafından desteklenmeyen bir formatta olabilir (ör. iPhone HEVC). Yeniden dene ya da yeni sekmede açıp indir.
            </div>
            <div className="flex gap-2 justify-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMediaError(false);
                  setLoading(true);
                  setRetryToken((t) => t + 1);
                }}
                data-testid={`reel-retry-${reel.id}`}
                className="text-[11px] font-black uppercase tracking-widest bg-[#CCFF00] text-[#0A0A0A] border-hard-2 rounded-lg px-3 py-1.5 press-btn"
              >
                Yeniden dene
              </button>
              <a
                href={absUrl(reel.media_url)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid={`reel-open-external-${reel.id}`}
                className="text-[11px] font-black uppercase tracking-widest bg-white text-[#0A0A0A] border-hard-2 rounded-lg px-3 py-1.5 press-btn inline-block"
              >
                Yeni sekmede aç
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Gradient overlays for readability */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/60 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />

      {/* Top badges */}
      <div className="absolute top-3 left-3 right-16 flex items-center gap-2 z-10">
        <div className="w-9 h-9 rounded-full bg-[#CCFF00] border-hard-2 flex items-center justify-center overflow-hidden font-display text-sm text-[#0A0A0A]">
          {reel.author?.picture ? <img src={absUrl(reel.author.picture)} alt="" className="w-full h-full object-cover" /> : (reel.author?.name || "?")[0].toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="font-black text-white text-sm truncate">{reel.author?.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-white/80">{humanAgo(reel.created_at)}</div>
        </div>
        <div className="ml-auto flex items-center gap-1">
          {reel.mode === "once" && (
            <span className="text-[10px] uppercase font-black tracking-widest bg-[#FF3B30] text-white border-hard-2 rounded-full px-2 py-0.5" data-testid={`reel-mode-once-${reel.id}`}>1x</span>
          )}
          <span className={`text-[10px] uppercase font-black tracking-widest border-hard-2 rounded-full px-2 py-0.5 ${reel.visibility === "public" ? "bg-white text-[#0A0A0A]" : "bg-[#D4B3FF] text-[#0A0A0A]"}`}>
            {reel.visibility === "public" ? <><Globe strokeWidth={3} className="w-2.5 h-2.5 inline mr-0.5" /> HERKES</> : <><Lock strokeWidth={3} className="w-2.5 h-2.5 inline mr-0.5" /> DOSTLAR</>}
          </span>
        </div>
      </div>

      {/* Mute toggle for videos */}
      {reel.media_type === "video" && (
        <button
          onClick={onToggleMute}
          data-testid={`reel-mute-${reel.id}`}
          className="absolute top-3 right-3 w-9 h-9 bg-white/90 text-[#0A0A0A] border-hard-2 rounded-full flex items-center justify-center z-10 press-btn"
          aria-label="Sesi aç/kapa"
        >
          {muted ? <VolumeX strokeWidth={3} className="w-4 h-4" /> : <Volume2 strokeWidth={3} className="w-4 h-4" />}
        </button>
      )}

      {/* Right action rail — right-aligned on both mobile and desktop, raised
          above the mobile bottom-nav (h≈80px). Kept clear of the Emergent
          watermark which lives inside the bottom-nav zone at bottom-right. */}
      <div className="absolute right-3 bottom-32 md:bottom-24 flex flex-col items-center gap-3 md:gap-4 z-10">
        <button
          onClick={() => onLike(reel)}
          data-testid={`reel-like-${reel.id}`}
          className={`w-12 h-12 rounded-full border-hard-2 flex items-center justify-center press-btn shadow-hard-sm ${reel.liked_by_me ? "bg-[#FF3B30] text-white" : "bg-white text-[#0A0A0A]"}`}
          aria-label="Beğen"
        >
          <Heart strokeWidth={3} className={`w-5 h-5 ${reel.liked_by_me ? "fill-white" : ""}`} />
        </button>
        <div className="text-xs font-black text-white drop-shadow -mt-3" data-testid={`reel-like-count-${reel.id}`}>{reel.like_count || 0}</div>

        {!reel.is_mine && (
          <button
            onClick={() => onOpenMessage(reel)}
            data-testid={`reel-msg-${reel.id}`}
            className="w-12 h-12 bg-white text-[#0A0A0A] border-hard-2 rounded-full flex items-center justify-center press-btn shadow-hard-sm"
            aria-label="Yayıncıya mesaj"
          >
            <MessageCircle strokeWidth={3} className="w-5 h-5" />
          </button>
        )}

        <button
          onClick={() => onOpenShare(reel)}
          data-testid={`reel-share-${reel.id}`}
          className="w-12 h-12 bg-white text-[#0A0A0A] border-hard-2 rounded-full flex items-center justify-center press-btn shadow-hard-sm"
          aria-label="Arkadaşa gönder"
        >
          <Send strokeWidth={3} className="w-5 h-5" />
        </button>

        <div className="text-xs font-black text-white drop-shadow flex items-center gap-1" title="Görüntülenme">
          <Eye strokeWidth={3} className="w-3.5 h-3.5" /> {reel.view_count || 0}
        </div>

        {reel.is_mine && (
          <button
            onClick={() => onDelete(reel)}
            data-testid={`reel-delete-${reel.id}`}
            className="w-11 h-11 bg-white/90 text-[#0A0A0A] border-hard-2 rounded-full flex items-center justify-center press-btn"
            aria-label="Sil"
          >
            <Trash2 strokeWidth={3} className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Bottom caption — clears the mobile bottom-nav and the right-side action rail. */}
      {reel.caption && (
        <div className="absolute left-3 right-20 bottom-24 md:bottom-4 z-10">
          <p className="text-white text-sm font-medium leading-snug drop-shadow whitespace-pre-wrap" data-testid={`reel-caption-${reel.id}`}>{reel.caption}</p>
        </div>
      )}
    </div>
  );
}

function MessageDialog({ reel, onClose, onSent }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/reels/${reel.id}/message`, { text: text.trim() });
      onSent?.();
      onClose();
    } catch (e) { setErr(formatApiError(e)); setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-end md:items-center justify-center p-4" data-testid="reel-message-dialog">
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-[#F9F8F6] border-hard shadow-hard-lg rounded-3xl p-4 w-full max-w-md"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl border-hard-2 overflow-hidden bg-[#CCFF00] flex items-center justify-center font-display">
            {reel.author?.picture ? <img src={absUrl(reel.author.picture)} alt="" className="w-full h-full object-cover" /> : (reel.author?.name || "?")[0].toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase font-black tracking-widest text-[#555]">Snap hakkında mesaj</div>
            <div className="font-display text-lg leading-tight">{reel.author?.name}</div>
          </div>
          <button onClick={onClose} data-testid="reel-msg-cancel" className="text-xs font-black uppercase tracking-widest border-hard-2 rounded-lg px-2 py-1 bg-white">İptal</button>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          data-testid="reel-msg-input"
          placeholder="Snap için ne söylemek istersin?"
          className="w-full bg-white border-hard rounded-xl px-3 py-2 resize-none focus:outline-none text-[16px]"
          autoFocus
        />
        {err && <div className="mt-2 border-hard-2 bg-[#FFD0C2] rounded-lg px-2 py-1 text-xs font-bold">{err}</div>}
        <div className="flex justify-end mt-3">
          <button
            onClick={send}
            disabled={sending || !text.trim()}
            data-testid="reel-msg-send"
            className="bg-[#CCFF00] border-hard shadow-hard rounded-xl px-4 py-2 font-black text-sm press-btn disabled:opacity-60"
          >
            {sending ? "…" : "Gönder"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default function ReelsPage() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [reels, setReels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const [messageFor, setMessageFor] = useState(null);
  const [shareFor, setShareFor] = useState(null);
  const [muted, setMuted] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/reels/feed");
      setReels(data);
      setActiveId(data[0]?.id || null);
    } catch (e) { setError(formatApiError(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // IntersectionObserver: track which reel card is centered.
  // Uses a dedicated data-reel-card attribute so we don't accidentally pick
  // up inner descendants whose testid also starts with 'reel-'.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const cards = container.querySelectorAll("[data-reel-card]");
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.intersectionRatio >= 0.6) {
            const id = e.target.getAttribute("data-reel-card");
            if (id) setActiveId(id);
          }
        }
      },
      { root: container, threshold: [0, 0.6, 1] }
    );
    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, [reels]);

  const onView = useCallback(async (reelId) => {
    try { await api.post(`/reels/${reelId}/view`); } catch {}
  }, []);

  const onLike = useCallback(async (reel) => {
    // Optimistic
    setReels((prev) => prev.map((r) => r.id === reel.id ? { ...r, liked_by_me: !r.liked_by_me, like_count: (r.like_count || 0) + (r.liked_by_me ? -1 : 1) } : r));
    try {
      const { data } = await api.post(`/reels/${reel.id}/like`);
      setReels((prev) => prev.map((r) => r.id === reel.id ? { ...r, liked_by_me: data.liked, like_count: data.like_count } : r));
    } catch (e) {
      alert(formatApiError(e));
      load();
    }
  }, [load]);

  const onDelete = useCallback(async (reel) => {
    if (!confirm("Bu snap silinsin mi?")) return;
    try {
      await api.delete(`/reels/${reel.id}`);
      setReels((prev) => prev.filter((r) => r.id !== reel.id));
    } catch (e) { alert(formatApiError(e)); }
  }, []);

  return (
    <div className="fixed inset-0 bg-[#0A0A0A] text-white overflow-hidden" data-testid="reels-page">
      <AppNav />

      {/* Vertical snap feed — desktop constrained to portrait width */}
      <div
        ref={containerRef}
        className="absolute inset-0 pb-20 md:pb-0 md:pl-[72px] overflow-y-auto snap-y snap-mandatory scrollbar-none"
        style={{ scrollbarWidth: "none" }}
        data-testid="reels-scroll"
      >
        <div className="mx-auto w-full h-full md:max-w-[420px] md:py-6">
          {loading && (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-white" />
            </div>
          )}

          {!loading && reels.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center px-6 text-center gap-4" data-testid="reels-empty">
              <div className="w-16 h-16 rounded-2xl bg-[#CCFF00] text-[#0A0A0A] border-hard shadow-hard flex items-center justify-center">
                <ChevronDown strokeWidth={3} className="w-6 h-6 rotate-180" />
              </div>
              <div>
                <div className="font-display text-2xl">Henüz snap yok</div>
                <div className="text-sm text-white/70 mt-1">İlk snap&apos;i sen paylaş, arkadaşların da katılsın.</div>
              </div>
              <button
                onClick={() => setComposerOpen(true)}
                data-testid="reels-empty-cta"
                className="bg-[#CCFF00] text-[#0A0A0A] border-hard shadow-hard rounded-xl px-5 py-3 font-black uppercase tracking-widest text-sm press-btn"
              >
                Yeni snap
              </button>
            </div>
          )}

          {error && (
            <div className="p-4">
              <div className="border-hard-2 bg-[#FFD0C2] text-[#0A0A0A] rounded-xl px-3 py-2 text-sm font-bold">{error}</div>
            </div>
          )}

          {reels.map((r, idx) => {
            const activeIdx = reels.findIndex((x) => x.id === activeId);
            const isActive = activeId === r.id;
            const isNear = activeIdx >= 0 && Math.abs(idx - activeIdx) === 1;
            return (
            <div key={r.id} data-reel-card={r.id} className="w-full h-full snap-start" style={{ minHeight: "100dvh" }}>
              <div className="w-full h-[100dvh] md:h-[calc(100dvh-48px)] md:rounded-3xl md:overflow-hidden md:border-hard md:shadow-hard-lg relative">
                <ReelCard
                  reel={r}
                  active={isActive}
                  near={isNear}
                  muted={muted}
                  onToggleMute={() => setMuted((m) => !m)}
                  onView={onView}
                  onLike={onLike}
                  onOpenMessage={setMessageFor}
                  onOpenShare={setShareFor}
                  onDelete={onDelete}
                />
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Compose FAB — mobile: top-right, well away from the bottom-nav +
          Emergent watermark zone. Desktop: bottom-right, standard position. */}
      <button
        onClick={() => setComposerOpen(true)}
        data-testid="reels-open-composer"
        className="fixed z-50 top-16 right-3 md:top-auto md:bottom-6 md:right-6 w-14 h-14 rounded-2xl bg-[#CCFF00] text-[#0A0A0A] border-hard shadow-hard flex items-center justify-center press-btn"
        aria-label="Yeni snap"
      >
        <Plus strokeWidth={3} className="w-6 h-6" />
      </button>

      <AnimatePresence>
        {composerOpen && (
          <ReelComposer
            onClose={() => setComposerOpen(false)}
            onPublished={(newReel) => {
              setComposerOpen(false);
              setReels((prev) => [newReel, ...prev]);
              setActiveId(newReel.id);
              containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        )}
      </AnimatePresence>

      {messageFor && (
        <MessageDialog reel={messageFor} onClose={() => setMessageFor(null)} onSent={() => navigate("/app/chats")} />
      )}

      {shareFor && (
        <ShareSheet reel={shareFor} onClose={() => setShareFor(null)} />
      )}
    </div>
  );
}
